import { useEffect, useRef, useState } from 'react'
import type { BlockVisibility, Viewport } from './types'

interface Props {
  value?: BlockVisibility
  onChange: (v: BlockVisibility | undefined) => void
}

const DEVICES: Viewport[] = ['desktop', 'tablet', 'mobile']

export function VisibilityEditor({ value, onChange }: Props) {
  const v = value ?? {}
  const set = (patch: Partial<BlockVisibility>) => {
    const next: BlockVisibility = { ...v, ...patch }
    // A schedule with neither end set is no schedule.
    if (next.schedule && !next.schedule.start && !next.schedule.end) next.schedule = undefined
    for (const k of Object.keys(next) as (keyof BlockVisibility)[]) {
      if (next[k] === undefined) delete next[k]
    }
    const empty = !next.devices?.length && !next.roles?.length && !next.schedule && !next.condition
    onChange(empty ? undefined : next)
  }

  // Roles are edited as free text and committed on blur / Enter. Parsing on
  // every keystroke re-joined the list as the user typed, so a trailing comma
  // vanished the instant it was typed and a second role could never start.
  const rolesText = (v.roles ?? []).join(', ')
  const [rolesDraft, setRolesDraft] = useState(rolesText)
  const editingRoles = useRef(false)
  useEffect(() => {
    // Follow external changes (undo, another block selected) unless the user
    // is mid-edit in this field.
    if (!editingRoles.current) setRolesDraft(rolesText)
  }, [rolesText])
  const commitRoles = () => {
    editingRoles.current = false
    const roles = rolesDraft.split(',').map(s => s.trim()).filter(Boolean)
    setRolesDraft(roles.join(', '))
    if (roles.join(',') !== (v.roles ?? []).join(',')) set({ roles: roles.length ? roles : undefined })
  }
  const toggleDevice = (d: Viewport) => {
    const cur = new Set(v.devices ?? DEVICES)
    cur.has(d) ? cur.delete(d) : cur.add(d)
    set({ devices: Array.from(cur) })
  }
  const visible = v.devices ? new Set(v.devices) : new Set(DEVICES)
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <Field label="Visible on">
        <div style={{ display: 'flex', gap: 6 }}>
          {DEVICES.map(d => (
            <label key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
              <input type="checkbox" checked={visible.has(d)} onChange={() => toggleDevice(d)} />
              {d}
            </label>
          ))}
        </div>
      </Field>
      <Field label="Roles (comma-separated; blank = all)">
        <input
          type="text"
          value={rolesDraft}
          onFocus={() => { editingRoles.current = true }}
          onChange={e => { editingRoles.current = true; setRolesDraft(e.target.value) }}
          onBlur={commitRoles}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitRoles() } }}
          placeholder="admin, manager"
          style={inputStyle}
        />
      </Field>
      <Field label="Schedule">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <input
            type="datetime-local"
            value={v.schedule?.start ?? ''}
            onChange={e => set({ schedule: { ...v.schedule, start: e.target.value || undefined } })}
            style={inputStyle}
          />
          <input
            type="datetime-local"
            value={v.schedule?.end ?? ''}
            onChange={e => set({ schedule: { ...v.schedule, end: e.target.value || undefined } })}
            style={inputStyle}
          />
        </div>
      </Field>
      <Field label="Condition (expression on inputs/vars)">
        <input
          type="text"
          value={v.condition ?? ''}
          onChange={e => set({ condition: e.target.value || undefined })}
          placeholder="{{count}} > 0"
          style={inputStyle}
        />
      </Field>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{label}</span>
      {children}
    </div>
  )
}
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: 13,
  border: '1px solid var(--color-border-strong)', borderRadius: 4,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
}
